import React, { useState, useEffect } from 'react';
import { useSupabase } from '../../supabaseAuth';
import AdminSubscriptionDetailModal from './AdminSubscriptionDetailModal';
import DeliveryModal from './DeliveryModal';

const SubscriptionsPage = () => {
  const { session, getAccessToken } = useSupabase();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filters, setFilters] = useState({
    status: '',
    metalType: '',
    minBalance: '',
    maxBalance: '',
    dateFrom: '',
    dateTo: ''
  });
  const [stats, setStats] = useState({
    totalSubscriptions: 0,
    activeSubscriptions: 0,
    pausedSubscriptions: 0,
    cancelledSubscriptions: 0,
    totalBalance: 0,
    totalValue: 0
  });
  const [metalPrices, setMetalPrices] = useState({});
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedDeliverySubscription, setSelectedDeliverySubscription] = useState(null);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);

  // Calculate balance ratio (progress as decimal)
  const calculateBalanceRatio = (subscription) => {
    // Use the subscription's target weight (weight field) as the target
    const targetWeight = subscription.weight || 1.0;
    
    // Use current_balance as the current progress
    const currentBalance = subscription.current_balance || 0;
    
    // Calculate ratio: current_balance / target_weight (no * 100)
    const ratio = currentBalance / targetWeight;
    
    return ratio;
  };

  // Calculate balance value (progress ratio * metal price)
  const calculateBalanceValue = (subscription) => {
    // Get the progress ratio (current_balance / target_weight)
    const ratio = calculateBalanceRatio(subscription);
    
    // Get the metal price
    const price = metalPrices[subscription.metal_type];
    if (!price) return 0;
    
    // Calculate value based on metal type
    let pricePerUnit;
    // For all metals, use price per ounce
    pricePerUnit = price.price_per_ounce;
    
    // Return progress ratio * metal price
    return ratio * pricePerUnit;
  };

  // Calculate progress percentage
  const calculateProgressPercentage = (subscription) => {
    // Get the metal price
    const price = metalPrices[subscription.metal_type];
    if (!price) return 0;
    
    // Calculate target cost based on actual weight_type from database
    let targetCost;
    const targetWeight = subscription.weight || 1.0;
    const weightType = subscription.weight_type || 'oz';
    
    if (weightType === 'g') {
      // For grams, use price per gram
      targetCost = targetWeight * price.price_per_gram * 1.15; // Include 15% markup
    } else if (weightType === 'kg') {
      // For kg, use price per gram * 1000
      const pricePerKg = price.price_per_gram * 1000;
      targetCost = targetWeight * pricePerKg * 1.15; // Include 15% markup
    } else {
      // For oz (default), use price per ounce
      targetCost = targetWeight * price.price_per_ounce * 1.15; // Include 15% markup
    }
    
    // Calculate percentage: current_balance / target_cost * 100
    const currentBalance = subscription.current_balance || 0;
    const percentage = (currentBalance / targetCost) * 100;
    
    return percentage;
  };

  // Fetch subscriptions from API
  const fetchSubscriptions = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Get the access token using the proper method
      const token = await getAccessToken();
      
      if (!token) {
        throw new Error('No authentication token available');
      }
      
      const queryParams = new URLSearchParams({
        search: searchTerm,
        sort_field: sortField,
        sort_direction: sortDirection,
        ...filters
      });

      // Get API base URL
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://api.getauric.dev';

      const response = await fetch(`${apiBaseUrl}/api/admin/subscriptions?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      // console.log('Subscriptions API response:', data); // Debug log
      setSubscriptions(data.subscriptions || []);
      
      // Calculate total value from current_balance if not provided by backend
      const calculatedStats = data.stats || {};
      if (!calculatedStats.totalValue && data.subscriptions) {
        calculatedStats.totalValue = data.subscriptions.reduce((total, sub) => {
          return total + (sub.current_balance || 0);
        }, 0);
      }
      
      setStats(calculatedStats);
      setMetalPrices(data.metal_prices || {});
    } catch (err) {
      setError(err.message);
      console.error('Error fetching subscriptions:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscriptions();
  }, [searchTerm, sortField, sortDirection, filters]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilters({
      status: '',
      metalType: '',
      minBalance: '',
      maxBalance: '',
      dateFrom: '',
      dateTo: ''
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatWeight = (weight, metalType, weightType) => {
    if (!weight) return '0';
    // Use the actual weight_type from database, or fall back to default
    const unit = weightType || 'oz';
    
    // Only show decimal if the number is not a whole number
    const formattedWeight = weight % 1 === 0 ? weight.toString() : weight.toFixed(4).replace(/\.?0+$/, '');
    return `${formattedWeight} ${unit}`;
  };

  const calculateCurrentValue = (balance, metalType, weightType) => {
    const price = metalPrices[metalType];
    if (!price || !balance) return 0;
    
    // Calculate how much metal can be bought with the current balance
    let metalAmount;
    
    // Use the actual weight_type from database to determine calculation
    if (weightType === 'g') {
      // For grams, use price per gram
      metalAmount = balance / price.price_per_gram;
    } else if (weightType === 'kg') {
      // For kg, use price per gram * 1000
      const pricePerKg = price.price_per_gram * 1000;
      metalAmount = balance / pricePerKg;
    } else {
      // For oz (default), use price per ounce
      metalAmount = balance / price.price_per_ounce;
    }
    
    return metalAmount;
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <i className="bi bi-arrow-down-up text-muted"></i>;
    return sortDirection === 'asc' 
      ? <i className="bi bi-arrow-up text-primary"></i>
      : <i className="bi bi-arrow-down text-primary"></i>;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'active': { class: 'bg-success', icon: '✅' },
      'paused': { class: 'bg-warning', icon: '⏸️' },
      'cancelled': { class: 'bg-danger', icon: '❌' },
      'pending': { class: 'bg-secondary', icon: '⏳' }
    };
    
    const config = statusConfig[status?.toLowerCase()] || statusConfig.pending;
    
    return (
      <span className={`badge ${config.class}`}>
        {status || 'Unknown'}
      </span>
    );
  };

  const getMetalIcon = (metalType) => {
    const icons = {
      'gold': '🥇',
      'silver': '🥈',
      'platinum': '⚪'
    };
    return icons[metalType?.toLowerCase()] || '💎';
  };

  const exportSubscriptions = async () => {
    try {
      // Get the access token using the proper method
      const token = await getAccessToken();
      
      if (!token) {
        throw new Error('No authentication token available');
      }
      
      // Get API base URL
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://api.getauric.dev';

      const response = await fetch(`${apiBaseUrl}/api/admin/subscriptions/export`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });

      if (!response.ok) {
        throw new Error('Export failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `subscriptions_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export error:', err);
      alert('Failed to export subscriptions');
    }
  };

  // Admin subscription management functions
  const pauseSubscription = async (subscriptionId) => {
    try {
      const token = await getAccessToken();
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://api.getauric.dev';

      const response = await fetch(`${apiBaseUrl}/api/admin/subscriptions/${subscriptionId}/pause`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to pause subscription: ${response.status}`);
      }

      // Refresh data
      fetchSubscriptions();
      return true;
    } catch (error) {
      console.error('Error pausing subscription:', error);
      throw error;
    }
  };

  const resumeSubscription = async (subscriptionId) => {
    try {
      const token = await getAccessToken();
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://api.getauric.dev';

      const response = await fetch(`${apiBaseUrl}/api/admin/subscriptions/${subscriptionId}/resume`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to resume subscription: ${response.status}`);
      }

      // Refresh data
      fetchSubscriptions();
      return true;
    } catch (error) {
      console.error('Error resuming subscription:', error);
      throw error;
    }
  };


  // Handle delivery modal
  const handleCreateDelivery = (subscription, event) => {
    // Remove focus from button to prevent it staying blue
    if (event && event.target) {
      event.target.blur();
    }
    setSelectedDeliverySubscription(subscription);
    setShowDeliveryModal(true);
  };

  const handleDeliveryCreated = (deliveryData) => {
    // console.log('Delivery created:', deliveryData);
    // Refresh data to update balance
    fetchSubscriptions();
    // You could show a success notification here
    alert(`Delivery created successfully! Transaction ID: ${deliveryData.transaction.id}`);
  };

  // Handle detail modal
  const handleViewDetails = (subscription, event) => {
    // Remove focus from button to prevent it staying blue
    if (event && event.target) {
      event.target.blur();
    }
    setSelectedSubscription(subscription);
    setShowDetailModal(true);
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setSelectedSubscription(null);
  };

  return (
    <div className="card">
      <div className="card-header">
        <div className="row align-items-center">
          <div className="col">
            <h4 className="mb-0">💰 Subscriptions</h4>
            <small className="text-muted">
              {stats.totalSubscriptions} total subscriptions • {formatCurrency(stats.totalValue)} total value
            </small>
          </div>
          <div className="col-auto">
            <div className="btn-group">
              <button 
                className="btn btn-outline-success btn-sm"
                onClick={exportSubscriptions}
                disabled={loading}
              >
                <i className="bi bi-download me-1"></i>
                Export CSV
              </button>
              <button 
                className="btn btn-outline-primary btn-sm"
                onClick={fetchSubscriptions}
                disabled={loading}
              >
                <i className="bi bi-arrow-clockwise me-1"></i>
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="card-body border-bottom">
        <div className="row g-3">
          <div className="col-md-3">
            <div className="card bg-primary text-white">
              <div className="card-body text-center py-3">
                <h5 className="card-title mb-0">{stats.totalSubscriptions}</h5>
                <small>Total Subscriptions</small>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-success text-white">
              <div className="card-body text-center py-3">
                <h5 className="card-title mb-0">{stats.activeSubscriptions}</h5>
                <small>Active</small>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-warning text-white">
              <div className="card-body text-center py-3">
                <h5 className="card-title mb-0">{stats.pausedSubscriptions}</h5>
                <small>Paused</small>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-info text-white">
              <div className="card-body text-center py-3">
                <h5 className="card-title mb-0">{formatCurrency(stats.totalValue)}</h5>
                <small>Total Value</small>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="card-body border-bottom">
        <div className="row g-3">
          <div className="col-md-3">
            <label className="form-label">Search</label>
            <div className="input-group">
              <span className="input-group-text">
                <i className="bi bi-search"></i>
              </span>
              <input 
                type="text" 
                className="form-control"
                placeholder="Search by user, email, or ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          
          <div className="col-md-2">
            <label className="form-label">Status</label>
            <select 
              className="form-select"
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          
          <div className="col-md-2">
            <label className="form-label">Metal Type</label>
            <select 
              className="form-select"
              value={filters.metalType}
              onChange={(e) => handleFilterChange('metalType', e.target.value)}
            >
              <option value="">All Metals</option>
              <option value="gold">Gold</option>
              <option value="silver">Silver</option>
              <option value="platinum">Platinum</option>
            </select>
          </div>
          
          <div className="col-md-2">
            <label className="form-label">Min Balance</label>
            <input 
              type="number" 
              className="form-control"
              placeholder="0.00"
              step="0.01"
              value={filters.minBalance}
              onChange={(e) => handleFilterChange('minBalance', e.target.value)}
            />
          </div>
          
          <div className="col-md-2">
            <label className="form-label">Max Balance</label>
            <input 
              type="number" 
              className="form-control"
              placeholder="1000.00"
              step="0.01"
              value={filters.maxBalance}
              onChange={(e) => handleFilterChange('maxBalance', e.target.value)}
            />
          </div>
          
          <div className="col-md-1 d-flex align-items-end">
            <button 
              className="btn btn-outline-secondary w-100"
              onClick={clearFilters}
              disabled={loading}
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="card-body p-0">
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Loading subscriptions...</p>
          </div>
        ) : error ? (
          <div className="alert alert-danger m-3" role="alert">
            <i className="bi bi-exclamation-triangle me-2"></i>
            Error loading subscriptions: {error}
          </div>
        ) : subscriptions.length === 0 ? (
          <div className="text-center py-5">
            <i className="bi bi-credit-card display-1 text-muted"></i>
            <p className="mt-3 text-muted">No subscriptions found</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th 
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('id')}
                  >
                    ID {getSortIcon('id')}
                  </th>
                  <th 
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('user_name')}
                  >
                    User {getSortIcon('user_name')}
                  </th>
                  <th 
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('metal_type')}
                  >
                    Metal {getSortIcon('metal_type')}
                  </th>
                  <th 
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('current_balance')}
                  >
                    Balance {getSortIcon('current_balance')}
                  </th>
                  <th>Current Value</th>
                  <th 
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('current_balance')}
                  >
                    Progress {getSortIcon('current_balance')}
                  </th>
                  <th 
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('amount_per_period')}
                  >
                    Amount/Period {getSortIcon('amount_per_period')}
                  </th>
                  <th 
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('status')}
                  >
                    Status {getSortIcon('status')}
                  </th>
                  <th 
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('next_billing_date')}
                  >
                    Next Billing {getSortIcon('next_billing_date')}
                  </th>
                  <th 
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('created_at')}
                  >
                    Created {getSortIcon('created_at')}
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {subscriptions.map((subscription) => {
                  const progressPercentage = calculateProgressPercentage(subscription);
                  
                  return (
                    <tr key={subscription.id}>
                      <td>
                        <code className="text-muted">#{subscription.id}</code>
                      </td>
                      <td>
                        <div>
                          <strong>{subscription.user_name}</strong>
                          <br />
                          <small className="text-muted">{subscription.user_email}</small>
                        </div>
                      </td>
                      <td>
                        <div>
                          <span className="me-2">{getMetalIcon(subscription.metal_type)}</span>
                          <span className="text-capitalize">{subscription.metal_type}</span>
                          <br />
                          <small className="text-muted">
                            Target: {formatWeight(subscription.weight, subscription.metal_type, subscription.weight_type)}
                          </small>
                        </div>
                      </td>
                      <td>
                        <strong>{formatCurrency(subscription.current_balance)}</strong>
                      </td>
                      <td>
                        <strong>
                          {formatWeight(calculateCurrentValue(subscription.current_balance, subscription.metal_type, subscription.weight_type), subscription.metal_type, subscription.weight_type)}
                        </strong>
                      </td>
                      <td>
                        <strong>
                          {progressPercentage.toFixed(1)}%
                        </strong>
                      </td>
                      <td>
                        <div>
                          <strong>{formatCurrency(subscription.amount_per_period)}</strong>
                          <br />
                          <small className="text-muted">per {subscription.period}</small>
                        </div>
                      </td>
                      <td>
                        {getStatusBadge(subscription.status)}
                      </td>
                      <td>
                        <small className="text-muted">
                          {formatDate(subscription.next_billing_date)}
                        </small>
                      </td>
                      <td>
                        <small className="text-muted">
                          {formatDate(subscription.created_at)}
                        </small>
                      </td>
                      <td>
                        <div className="btn-group btn-group-sm">
                          <button 
                            className="btn btn-outline-primary"
                            title="View Details"
                            onClick={(e) => handleViewDetails(subscription, e)}
                            style={{ 
                              outline: 'none !important', 
                              boxShadow: 'none !important',
                              backgroundColor: 'transparent !important',
                              borderColor: '#6c757d !important'
                            }}
                            onFocus={(e) => e.target.blur()}
                            onMouseDown={(e) => e.preventDefault()}
                          >
                            <i className="bi bi-eye"></i>
                          </button>
                          {subscription.status === 'active' && (
                            <button 
                              className="btn btn-outline-warning"
                              title="Pause Subscription"
                              onClick={() => {
                                if (window.confirm('Are you sure you want to pause this subscription?')) {
                                  pauseSubscription(subscription.id).catch(err => 
                                    alert('Failed to pause subscription: ' + err.message)
                                  );
                                }
                              }}
                            >
                              <i className="bi bi-pause-circle"></i>
                            </button>
                          )}
                          {subscription.status === 'paused' && (
                            <button 
                              className="btn btn-outline-success"
                              title="Resume Subscription"
                              onClick={() => {
                                if (window.confirm('Are you sure you want to resume this subscription?')) {
                                  resumeSubscription(subscription.id).catch(err => 
                                    alert('Failed to resume subscription: ' + err.message)
                                  );
                                }
                              }}
                            >
                              <i className="bi bi-play-circle"></i>
                            </button>
                          )}
                          {subscription.status === 'active' && subscription.current_balance > 0 && (
                            <button 
                              className="btn btn-outline-info"
                              title="Create Delivery"
                              onClick={(e) => handleCreateDelivery(subscription, e)}
                            >
                              <i className="bi bi-truck"></i>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      {/* Subscription Detail Modal */}
      {selectedSubscription && (
        <AdminSubscriptionDetailModal
          show={showDetailModal}
          onHide={handleCloseDetailModal}
          subscription={selectedSubscription}
          metalPrices={metalPrices}
          onPause={pauseSubscription}
          onResume={resumeSubscription}
        />
      )}
      
      {/* Delivery Modal */}
      {selectedDeliverySubscription && (
        <DeliveryModal
          subscription={selectedDeliverySubscription}
          show={showDeliveryModal}
          onHide={() => {
            setShowDeliveryModal(false);
            setSelectedDeliverySubscription(null);
          }}
          onDeliveryCreated={handleDeliveryCreated}
        />
      )}
    </div>
  );
};

export default SubscriptionsPage;



